/**
 * One-line descriptions for the providers a user can connect.
 *
 * These live in the console because `CatalogEntry` carries no `description`
 * field. If the catalog gains one, prefer it and treat this as the fallback —
 * `describeProvider` already takes the catalog value first, so that change is a
 * one-line edit at the call site rather than a rewrite.
 *
 * An unmapped provider returns `undefined` and its card renders without a
 * description, rather than showing a placeholder or a generic sentence that
 * would say nothing.
 *
 * Copy describes what connecting the app lets Moo do, in the user's terms —
 * not what the integration is.
 */
const PROVIDER_DESCRIPTIONS: Record<string, string> = {
  google_calendar: 'Check your availability and upcoming meetings',
  gmail: 'Search and summarise your email',
  google_drive: 'Find and read your documents',
  zoom: 'Look up your meetings and recordings',
  atlassian_jira: 'Search your issues, projects and current work',
  atlassian_confluence: 'Search the pages and spaces you can access',
  slack: 'Search chats and channels and find key information',
  github: 'Host code and collaborate on projects',
  salesforce: 'Look up leads, contacts, accounts and opportunities',
  datadog: 'Query logs, metrics and monitors',
  'cyber-pulse': 'Bring in Cowbell cyber risk intelligence',
  custom_mcp: 'Connect a custom MCP server',
};

/**
 * Description for a provider card, preferring anything the catalog supplies.
 *
 * @param provider  Backend provider key, e.g. `google_calendar`.
 * @param fromCatalog  A catalog-supplied description, when one exists.
 */
export function describeProvider(
  provider: string,
  fromCatalog?: string
): string | undefined {
  const catalogCopy = fromCatalog?.trim();
  if (catalogCopy) return catalogCopy;
  return PROVIDER_DESCRIPTIONS[provider];
}

export { PROVIDER_DESCRIPTIONS };
